import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import {
  fetchVimeoThumbnailUrl,
  getYoutubeThumbnailUrl,
  parseHelpVideoUrl,
} from '../../utils/help-video-url.util';
import {
  HelpVideoPlayerData,
  HelpVideoPlayerDialogComponent,
} from './help-video-player-dialog/help-video-player-dialog.component';

export interface HelpVideo {
  titleKey: string;
  descriptionKey: string;
  /**
   * Optional YouTube / Vimeo / direct video file link. Validated with `parseHelpVideoUrl`;
   * anything it rejects is not played.
   */
  linkUrl?: string | null;
  /**
   * Optional URL of an uploaded video file. Takes precedence over `linkUrl`.
   */
  fileUrl?: string | null;
}

interface ResolvedHelpVideo {
  video: HelpVideo;
  // Embed URLs are rebuilt by parseHelpVideoUrl from a validated YouTube/Vimeo id
  embedUrl: SafeResourceUrl | null;
  fileUrl: string | null;
  // Still image shown before playback (YouTube / Vimeo). Files use their first frame instead.
  thumbnailUrl: string | null;
  vimeoId: string | null;
}

@Component({
  selector: 'app-help-videos',
  standalone: false,
  templateUrl: './help-videos.component.html',
  styleUrl: './help-videos.component.scss',
})
export class HelpVideosComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);

  /**
   * Translation key for the section title
   */
  title = input<string>('contact.videos.title');

  /**
   * Translation key for the section description
   */
  description = input<string>('contact.videos.description');

  /**
   * Videos to display in the carousel
   */
  videos = input<HelpVideo[]>([]);

  /**
   * Emits the video the user clicked
   */
  videoSelected = output<HelpVideo>();

  // One dot per scroll position: each arrow click moves exactly one card, i.e. one dot
  readonly dotCount = signal(1);
  readonly activeDot = signal(0);
  readonly dots = computed(() => Array.from({ length: this.dotCount() }, (_, i) => i));

  // Arrows + dots appear only when there are more videos than fit per view (3 desktop,
  // 2 tablet, 1 mobile — see .video-card). Arrows are additionally hidden on mobile via CSS.
  readonly showNavigation = computed(() => this.dotCount() > 1);

  // Resolved once per input change so the sanitized embed URL keeps a stable
  // identity (a new SafeResourceUrl on every change detection reloads the iframe)
  readonly resolvedVideos = computed<ResolvedHelpVideo[]>(() =>
    this.videos().map((video) => this.resolve(video))
  );

  // Vimeo thumbnails, looked up asynchronously and keyed by video id
  private readonly vimeoThumbnails = signal<Record<string, string>>({});
  private readonly requestedVimeoIds = new Set<string>();

  private track = viewChild<ElementRef<HTMLDivElement>>('track');
  private readonly injector = inject(Injector);

  constructor() {
    // Recount dots once the cards for a new video list have rendered
    effect(() => {
      this.resolvedVideos();
      afterNextRender(() => this.updateDots(), { injector: this.injector });
    });

    effect(() => {
      const vimeoIds = this.resolvedVideos()
        .map((item) => item.vimeoId)
        .filter((id): id is string => !!id && !this.requestedVimeoIds.has(id));
      untracked(() => vimeoIds.forEach((id) => this.loadVimeoThumbnail(id)));
    });
  }

  thumbnailFor(item: ResolvedHelpVideo): string | null {
    return (
      item.thumbnailUrl ?? (item.vimeoId ? (this.vimeoThumbnails()[item.vimeoId] ?? null) : null)
    );
  }

  // '#t=0.1' makes browsers paint the first frame instead of a blank/black box
  posterFrameUrl(fileUrl: string): string {
    return fileUrl.includes('#') ? fileUrl : `${fileUrl}#t=0.1`;
  }

  onVideoClick(video: HelpVideo, index?: number): void {
    const resolved = index !== undefined ? this.resolvedVideos()[index] : null;
    if (resolved && (resolved.embedUrl || resolved.fileUrl)) {
      this.openPlayer(resolved);
    }
    this.videoSelected.emit(video);
  }

  // Full-viewport player; MatDialog closes it on Esc, the dialog itself on X / outside clicks
  private openPlayer(item: ResolvedHelpVideo): void {
    this.dialog.open<HelpVideoPlayerDialogComponent, HelpVideoPlayerData>(
      HelpVideoPlayerDialogComponent,
      {
        data: { titleKey: item.video.titleKey, embedUrl: item.embedUrl, fileUrl: item.fileUrl },
        panelClass: 'help-video-player-dialog',
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        ariaLabelledBy: 'help-video-player-title',
      }
    );
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDots();
  }

  // Also fires for swipes/trackpad scrolling, so the dots never drift from the cards
  onTrackScroll(): void {
    this.updateDots();
  }

  scrollPrev(): void {
    this.scrollByCard(-1);
  }

  scrollNext(): void {
    this.scrollByCard(1);
  }

  private resolve(video: HelpVideo): ResolvedHelpVideo {
    const empty = { embedUrl: null, fileUrl: null, thumbnailUrl: null, vimeoId: null };
    const fileUrl = video.fileUrl?.trim() || null;
    if (fileUrl) return { ...empty, video, fileUrl };

    const preview = video.linkUrl ? parseHelpVideoUrl(video.linkUrl) : null;
    if (preview?.kind === 'embed') {
      const separator = preview.embedUrl.includes('?') ? '&' : '?';
      return {
        ...empty,
        video,
        embedUrl: this.sanitizer.bypassSecurityTrustResourceUrl(
          `${preview.embedUrl}${separator}autoplay=1`
        ),
        thumbnailUrl:
          preview.provider === 'youtube' ? getYoutubeThumbnailUrl(preview.videoId) : null,
        vimeoId: preview.provider === 'vimeo' ? preview.videoId : null,
      };
    }
    if (preview?.kind === 'file') return { ...empty, video, fileUrl: preview.fileUrl };

    return { ...empty, video };
  }

  private async loadVimeoThumbnail(videoId: string): Promise<void> {
    this.requestedVimeoIds.add(videoId);
    const url = await fetchVimeoThumbnailUrl(videoId);
    if (url) this.vimeoThumbnails.update((thumbs) => ({ ...thumbs, [videoId]: url }));
  }

  private scrollByCard(direction: 1 | -1): void {
    const trackEl = this.track()?.nativeElement;
    if (!trackEl) return;
    trackEl.scrollBy({ left: direction * this.cardStep(trackEl), behavior: 'smooth' });
  }

  // Distance between the start of one card and the next (card width + 20px track gap)
  private cardStep(trackEl: HTMLElement): number {
    const card = trackEl.querySelector<HTMLElement>('.video-card');
    return card ? card.offsetWidth + 20 : trackEl.clientWidth;
  }

  private updateDots(): void {
    const trackEl = this.track()?.nativeElement;
    if (!trackEl) return;
    const step = this.cardStep(trackEl);
    if (step <= 0) return;

    const maxIndex = Math.max(0, Math.round((trackEl.scrollWidth - trackEl.clientWidth) / step));
    this.dotCount.set(maxIndex + 1);
    this.activeDot.set(Math.min(maxIndex, Math.round(trackEl.scrollLeft / step)));
  }
}
